import { WithDisposable } from '@blocksuite/block-std';
import { html, LitElement } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';

import { stopPropagation } from '../../../../_common/utils/event.js';
import { CHROMA_STYLE } from './styles.js';
import {
  clamp,
  FIRST_COLOR,
  type Hsv,
  hsvToRgb,
  hsvToRgbaString,
  LINEAR_GRADIENT,
  linearGradientAt,
  rgbToHex,
  rgbToHsv,
} from './utils.js';

type NavType = 'colors' | 'custom';

type ModeType = 'light' | 'dark';

type Tab<Type> = {
  type: Type;
  name: string;
  actived: boolean;
};

@customElement('edgeless-chroma')
export class EdgelessChroma extends WithDisposable(LitElement) {
  static override styles = CHROMA_STYLE;

  @state()
  accessor navType: NavType = 'colors';

  @state()
  accessor modeType: ModeType = 'light';

  @state()
  accessor _areaOffset = { x: 0, y: 0 };

  @state()
  accessor _sliderOffset = { x: 0, y: 0 };

  // HSV, fallback COLORS[0]
  @state()
  accessor _color: Hsv = FIRST_COLOR;

  // Alpha: [0, 1]
  @state()
  accessor _opacity = 1;

  @query('.color-area')
  accessor area!: HTMLDivElement;

  @query('.color-slider')
  accessor slider!: HTMLDivElement;

  get hexString() {
    return rgbToHex(hsvToRgb(this._color));
  }

  override firstUpdated() {
    this.disposables.addFromEvent(this, 'pointerdown', stopPropagation);
    this.disposables.addFromEvent(this, 'click', stopPropagation);

    let areaClicked = false;
    this.disposables.addFromEvent(this.area, 'pointerdown', (e: MouseEvent) => {
      areaClicked = true;
      const x = clamp(0, e.offsetX - 10.5, 198);
      const y = clamp(0, e.offsetY - 10.5, 170);
      this._areaOffset = {
        x,
        y,
      };
    });
    this.disposables.addFromEvent(this.area, 'pointermove', (e: MouseEvent) => {
      if (!areaClicked) return;

      const x = clamp(0, e.offsetX - 10.5, 198);
      const y = clamp(0, e.offsetY - 10.5, 170);
      this._areaOffset = {
        x,
        y,
      };
    });
    this.disposables.addFromEvent(this.area, 'pointerup', (_: MouseEvent) => {
      areaClicked = false;
    });

    let sliderClicked = false;
    this.disposables.addFromEvent(
      this.slider,
      'pointerdown',
      (e: MouseEvent) => {
        sliderClicked = true;
        const x = clamp(0, e.offsetX - 8, 198);
        const { h } = rgbToHsv(linearGradientAt(x / 198));
        this._color = {
          ...this._color,
          h,
        };
        this._sliderOffset = {
          x,
          y: 0,
        };
      }
    );
    this.disposables.addFromEvent(
      this.slider,
      'pointermove',
      (e: MouseEvent) => {
        if (!sliderClicked) return;
        const x = clamp(0, e.offsetX - 8, 198);
        const { h } = rgbToHsv(linearGradientAt(x / 198));
        this._color = {
          ...this._color,
          h,
        };
        this._sliderOffset = {
          x,
          y: 0,
        };
      }
    );
    this.disposables.addFromEvent(this.slider, 'pointerup', (_: MouseEvent) => {
      sliderClicked = false;
    });
  }

  override render() {
    const activedCustom = this.navType === 'custom';
    const activedLight = this.modeType === 'light';
    const tabs: Tab<NavType>[] = [
      {
        type: 'colors',
        name: 'Colors',
        actived: !activedCustom,
      },
      {
        type: 'custom',
        name: 'Custom',
        actived: activedCustom,
      },
    ];
    const modes: Tab<ModeType>[] = [
      {
        type: 'light',
        name: 'Light',
        actived: activedLight,
      },
      {
        type: 'dark',
        name: 'Dark',
        actived: !activedLight,
      },
    ];

    return html`
      <header>
        <nav>
          ${repeat(
            tabs,
            tab => tab.type,
            ({ type, name, actived }) => html`
              <button ?actived=${actived} @click=${() => (this.navType = type)}>
                ${name}
              </button>
            `
          )}
        </nav>
      </header>

      <div class="modes" ?actived=${activedCustom}>
        ${repeat(
          modes,
          mode => mode.type,
          ({ type, name, actived }) => html`
            <div
              class="${classMap({
                mode: true,
                [type]: true,
              })}"
            >
              <button
                ?actived=${actived}
                @click=${() => (this.modeType = type)}
              >
                <div class="color"></div>
                <div>${name}</div>
              </button>
            </div>
          `
        )}
      </div>

      <div class="content">
        <div
          class="color-area-wrapper"
          style=${styleMap({
            '--x': `${this._areaOffset.x}px`,
            '--y': `${this._areaOffset.y}px`,
            '--c': `${hsvToRgbaString(this._color)}`,
          })}
        >
          <div class="color-circle"></div>
          <div class="color-area"></div>
        </div>
        <div
          class="color-slider-wrapper"
          style=${styleMap({
            '--colors': LINEAR_GRADIENT,
            '--x': `${this._sliderOffset.x}px`,
            '--c': `${hsvToRgbaString(this._color)}`,
          })}
        >
          <div class="color-circle"></div>
          <div class="color-slider"></div>
        </div>
      </div>

      <footer>
        <div class="field color">
          <label for="color-value">#</label>
          <input id="color-value" type="text" .value=${this.hexString} />
        </div>
        <div class="field opacity">
          <input
            type="number"
            min="0"
            max="100"
            .value=${this._opacity * 100}
          />
          <label for="color-value">%</label>
        </div>
      </footer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edgeless-chroma': EdgelessChroma;
  }
}
